import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  ChevronDown, ChevronRight, User, Calendar,
  CheckCircle, XCircle, Clock, FileText
} from 'lucide-react';
import api from '../services/api';

const EditRequestsList = ({ permitId }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Fetch all edit requests for this permit
  const { data: editRequestsData, isLoading } = useQuery(
    ['editRequests', permitId],
    async () => {
      const response = await api.get(`/edit-requests/permit/${permitId}/all`);
      return response.data;
    },
    {
      enabled: isExpanded // Only fetch when expanded
    }
  );

  const statusConfig = {
    'Pending_EAC_Review': {
      label: 'Pending EAC Review',
      color: 'bg-blue-100 text-blue-800',
      icon: Clock
    },
    'Pending_SDO_Review': {
      label: 'Pending SDO Review',
      color: 'bg-blue-100 text-blue-800',
      icon: Clock
    },
    'Pending_ADC_Review': {
      label: 'Pending ADC Review',
      color: 'bg-blue-100 text-blue-800',
      icon: Clock
    },
    'Pending_DC_Approval': {
      label: 'Pending DC Approval',
      color: 'bg-blue-100 text-blue-800',
      icon: Clock
    },
    'Approved': {
      label: 'Approved & Merged',
      color: 'bg-green-100 text-green-800',
      icon: CheckCircle
    },
    'Rejected': {
      label: 'Rejected',
      color: 'bg-red-100 text-red-800',
      icon: XCircle
    },
    'Cancelled': {
      label: 'Cancelled',
      color: 'bg-gray-100 text-gray-800',
      icon: XCircle
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangeSummary = (editRequest) => {
    const parts = [];
    const changedFields = Object.keys(editRequest.requestedChanges || {});
    if (changedFields.length > 0) {
      parts.push(`${changedFields.length} field(s)`);
    }
    const docChanges = editRequest.documentChanges || {};
    const docCount = (docChanges.landRegistryDocument ? 1 : 0) +
                     (docChanges.registryMapDocument ? 1 : 0) +
                     (docChanges.scannedDocuments?.length || 0);
    if (docCount > 0) {
      parts.push(`${docCount} document(s)`);
    }
    return parts.join(', ') || 'No changes';
  };

  const editRequests = editRequestsData?.editRequests || [];

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Accordion Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-500" />
          )}
          <FileText className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Edit Request History
          </h3>
        </div>
        {!isExpanded && editRequests.length > 0 && (
          <span className="text-sm text-gray-500">
            {editRequests.length} request{editRequests.length !== 1 ? 's' : ''}
          </span>
        )}
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className="border-t">
          {isLoading ? (
            <div className="px-6 py-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : editRequests.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No edit requests found for this permit</p>
            </div>
          ) : (
            <div className="divide-y">
              {editRequests.map((editRequest) => {
                const statusInfo = statusConfig[editRequest.status] || {
                  label: editRequest.status,
                  color: 'bg-gray-100 text-gray-800',
                  icon: Clock
                };
                const StatusIcon = statusInfo.icon;

                return (
                  <div key={editRequest.id} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Initiator & Date */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900">
                            {editRequest.initiator?.fullName || 'Unknown'}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({editRequest.initiatorRole?.toUpperCase()})
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-600">
                            Created: {formatDate(editRequest.createdAt)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 mb-2">
                          Changes: {getChangeSummary(editRequest)}
                        </div>
                        {editRequest.reason && (
                          <div className="text-sm text-gray-500 italic line-clamp-2">
                            "{editRequest.reason}"
                          </div>
                        )}
                      </div>

                      {/* Right: Status & Decision */}
                      <div className="flex flex-col items-end gap-2">
                        {/* Status Badge */}
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
                          <StatusIcon className="h-4 w-4 mr-1.5" />
                          {statusInfo.label}
                        </span>

                        {/* Final Decision Details */}
                        {editRequest.status === 'Approved' && editRequest.approver && (
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Approved by</div>
                            <div className="text-sm font-medium text-green-700">
                              {editRequest.approver.fullName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatDate(editRequest.approvedAt)}
                            </div>
                          </div>
                        )}

                        {editRequest.status === 'Rejected' && editRequest.rejector && (
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Rejected by</div>
                            <div className="text-sm font-medium text-red-700">
                              {editRequest.rejector.fullName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatDate(editRequest.rejectedAt)}
                            </div>
                            {editRequest.rejectionReason && (
                              <div className="text-xs text-gray-600 italic mt-1 max-w-xs">
                                "{editRequest.rejectionReason}"
                              </div>
                            )}
                          </div>
                        )}

                        {/* View Link */}
                        <Link
                          to={`/edit-requests/${editRequest.id}`}
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          View Details →
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EditRequestsList;
